import React, {Dispatch, MutableRefObject, SetStateAction, useMemo} from "react"
import {TabPane, Icon} from "semantic-ui-react"
import {I18nIndividual as GedcomIndividual} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {CreatorIndiTable as CreatorIndi} from "./individuals/creator-indi-table"
import {CreatorFamTable as CreatorFam} from "./families/creator-fam-table"
import {MenuItem} from "../menu/menu-item"
import {FormattedMessage} from "react-intl"
import {restoredSession} from "../utils/creator-utils"


interface CreatorPanesProps {
    activeTab: number
    from?: string | null
    lastIndividualId: MutableRefObject<number>
    individualsData: GedcomIndividual[]
    lastFamilyId: MutableRefObject<number>
    familiesData: GedcomFamily[]
    familiesEnabled: boolean
    switchTab: (targetRowId: string) => void
    setIndividualsData: Dispatch<SetStateAction<GedcomIndividual[]>>
    setFamiliesData: Dispatch<SetStateAction<GedcomFamily[]>>
    familiesAnnounceDone: MutableRefObject<boolean>
    dismissFamiliesAnnounce: () => void
    startFamily: (fatherId: string, motherId: string) => void
    setUnsavedChanges?: Dispatch<SetStateAction<boolean>>
    setContactModalOpen?: Dispatch<SetStateAction<boolean>>
}

export function CreatorPanes({
    activeTab,
    from,
    lastIndividualId,
    individualsData,
    lastFamilyId,
    familiesData,
    familiesEnabled,
    switchTab,
    setIndividualsData,
    setFamiliesData,
    familiesAnnounceDone,
    dismissFamiliesAnnounce,
    startFamily,
    setUnsavedChanges,
    setContactModalOpen
}: CreatorPanesProps) {
    return useMemo(() => [
        {
            // INDIVIDUALS
            menuItem:
                <MenuItem id="individuals-menu" key="individuals" style={{justifyContent: "center"}}>
                    <Icon name="user"/>&nbsp;
                    <FormattedMessage id="creator.tabs.individuals"/>
                </MenuItem>,
            pane:
                <TabPane key="individuals" className={activeTab === 0 ? "active-pane" : ""}>
                    <CreatorIndi
                        data={individualsData}
                        lastId={lastIndividualId}
                        familiesData={familiesData}
                        familiesEnabled={familiesEnabled}
                        eventHandlers={{ setIndividualsData, setFamiliesData, startFamily, switchTab, setUnsavedChanges, setContactModalOpen }}
                    />
                </TabPane>
        }, {
            // FAMILIES
            menuItem:
                <MenuItem
                    id="families-menu"
                    key="families"
                    style={{justifyContent: "center"}}
                    disabled={!familiesEnabled}
                    className={
                        familiesEnabled &&                      // there are families
                        !familiesAnnounceDone.current &&        // the announcement hasn't happened already
                        !from &&                                // not coming from /view nor /import
                        !restoredSession().restoredFamilies &&  // if data is restored from a previous session
                        !restoredSession().restoredIndividuals
                        ? "families-enabled" : ""
                    }
                    onClick={() => {
                        dismissFamiliesAnnounce()
                        familiesAnnounceDone.current = true
                    }}
                >
                    <FormattedMessage id="creator.tabs.families"/>&nbsp;&nbsp;
                    <Icon name="users"/>
                </MenuItem>,
            pane:
                <TabPane key="families" className={activeTab === 1 ? "active-pane" : ""}>
                    <CreatorFam
                        data={familiesData}
                        lastId={lastFamilyId}
                        individualsData={individualsData}
                        eventHandlers={{ setIndividualsData, setFamiliesData, switchTab, setUnsavedChanges, setContactModalOpen }}
                    />
                </TabPane>
        }
    ], [
        activeTab,
        from,
        lastIndividualId,
        individualsData,
        setIndividualsData,
        lastFamilyId,
        familiesData,
        setFamiliesData,
        familiesEnabled,
        startFamily,
        switchTab,
        familiesAnnounceDone,
        dismissFamiliesAnnounce,
        setUnsavedChanges,
        setContactModalOpen
    ])
}